import React from 'react';
import noop from 'lodash/noop';
import {
	Aside,
	Banner as NewBanner,
	Main as NewMain,
	Panel,
	Root,
	SideNav,
	TopNav,
	useIsFhsEnabled,
} from '@atlaskit/navigation-system';
import {
	type usePageLayoutResize as useLegacyPageLayoutResize,
	BANNER_HEIGHT,
	LEFT_PANEL_WIDTH,
	LEFT_SIDEBAR_WIDTH,
	RIGHT_PANEL_WIDTH,
	RIGHT_SIDEBAR_WIDTH,
	TOP_NAVIGATION_HEIGHT,
} from '@atlaskit/page-layout';

import { fg } from '@atlassian/jira-feature-gating';

// Common prop types used across multiple components
type BaseComponentProps = {
	testId?: string;
	children?: React.ReactNode;
};

type SizeableComponentProps = BaseComponentProps & {
	width?: number;
};

export type PageLayoutProps = BaseComponentProps & {
	defaultSideNavCollapsed?: boolean;
	isSideNavShortcutEnabled?: boolean;
};

export function PageLayout({
	testId,
	children,
	defaultSideNavCollapsed,
	isSideNavShortcutEnabled,
}: PageLayoutProps): React.JSX.Element {
	const isFhsEnabled = fg('navx-2731-convert-jira-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	return (
		<Root
			UNSAFE_dangerouslyHoistSlotSizes
			testId={testId}
			defaultSideNavCollapsed={isFhsEnabled ? defaultSideNavCollapsed : undefined}
			isSideNavShortcutEnabled={isSideNavShortcutEnabled}
		>
			{children}
		</Root>
	);
}

type BannerProps = BaseComponentProps & {
	height?: number;
	id?: string;
};

export function Banner({ height, testId, id, children }: BannerProps): React.JSX.Element {
	return (
		<NewBanner height={height} testId={testId} id={id}>
			{children}
		</NewBanner>
	);
}

type TopNavigationProps = BaseComponentProps & {
	height?: number;
	/**
	 * The Nav4 theme config.
	 */
	theme?: React.ComponentProps<typeof TopNav>['UNSAFE_theme'];
};

export function TopNavigation({
	height,
	testId,
	theme,
	children,
}: TopNavigationProps): React.JSX.Element {
	return (
		<TopNav height={height} testId={testId} UNSAFE_theme={theme}>
			{children}
		</TopNav>
	);
}

export function LeftSidebarWithoutResize({
	width,
	testId,
	children,
}: SizeableComponentProps): React.JSX.Element {
	return (
		<SideNav defaultWidth={width} testId={testId}>
			{children}
		</SideNav>
	);
}

type LeftSidebarProps = SizeableComponentProps & {
	collapsedState?: 'collapsed' | 'expanded';
};

export function LeftSidebar({
	collapsedState,
	width,
	testId,
	children,
}: LeftSidebarProps): React.JSX.Element {
	const isFhsEnabled = fg('navx-2731-convert-jira-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	return (
		<SideNav
			defaultCollapsed={isFhsEnabled ? undefined : collapsedState === 'collapsed'}
			defaultWidth={width}
			testId={testId}
		>
			{children}
		</SideNav>
	);
}

export function Main({ testId, children }: BaseComponentProps): React.JSX.Element {
	return <NewMain testId={testId}>{children}</NewMain>;
}

export function RightSidebar({
	width,
	testId,
	children,
}: SizeableComponentProps): React.JSX.Element {
	return (
		<Aside defaultWidth={width} testId={testId}>
			{children}
		</Aside>
	);
}

export function RightPanel({ width, testId, children }: SizeableComponentProps): React.JSX.Element {
	return (
		<Panel defaultWidth={width} testId={testId}>
			{children}
		</Panel>
	);
}

// A mock value is needed to match the signature of the hook
const mockPageLayoutResize: ReturnType<typeof useLegacyPageLayoutResize> = {
	isLeftSidebarCollapsed: false,
	expandLeftSidebar: noop,
	collapseLeftSidebar: noop,
	leftSidebarState: {
		isFlyoutOpen: false,
		isResizing: false,
		isLeftSidebarCollapsed: false,
		leftSidebarWidth: 0,
		lastLeftSidebarWidth: 0,
		flyoutLockCount: 0,
		isFixed: true,
		hasInit: false,
	},
	toggleLeftSidebar: noop,
};

export const usePageLayoutResize: typeof useLegacyPageLayoutResize = () => mockPageLayoutResize;

export const UNSAFE_DO_NOT_USE_BANNER_HEIGHT: 'var(--bannerHeight, 0px)' = BANNER_HEIGHT;
export const UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH: 'var(--leftPanelWidth, 0px)' = LEFT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH: 'var(--leftSidebarWidth, 0px)' =
	LEFT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH: 'var(--rightPanelWidth, 0px)' = RIGHT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH: 'var(--rightSidebarWidth, 0px)' =
	RIGHT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT: 'var(--topNavigationHeight, 0px)' =
	TOP_NAVIGATION_HEIGHT;
